import { cn } from '@/lib/cn'

/**
 * An order's state, in a shopper's words rather than the warehouse's.
 *
 * Not `features/orders/StatusBadge`, and deliberately so. That component speaks to staff:
 * `submitted` reads "Awaiting approval", which is true internally and meaningless to
 * somebody who has just paid — they did not submit anything for approval, they bought
 * something. The storefront translates the same seven statuses into what happens next for
 * the person waiting for a parcel.
 *
 * Colour carries meaning, so the label always says it too: "Cancelled" and "Being prepared"
 * must be distinguishable by someone who cannot tell amber from red.
 */
const ORDER_TONES: Record<string, string> = {
  draft: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  submitted: 'bg-warning/10 text-warning',
  approved: 'bg-brand-500/10 text-brand-600 dark:text-brand-400',
  fulfilled: 'bg-success/10 text-success',
  invoiced: 'bg-success/10 text-success',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-danger/10 text-danger',
}

const ORDER_LABELS: Record<string, string> = {
  draft: 'Awaiting payment',
  submitted: 'Confirmed',
  approved: 'Being prepared',
  fulfilled: 'Dispatched',
  invoiced: 'Dispatched',
  completed: 'Delivered',
  cancelled: 'Cancelled',
}

const PAYMENT_TONES: Record<string, string> = {
  paid: 'bg-success/10 text-success',
  unpaid: 'bg-warning/10 text-warning',
  refunded: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}

const PAYMENT_LABELS: Record<string, string> = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  refunded: 'Refunded',
}

const base = 'rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap'

/** Falls back to the raw value rather than rendering an empty pill: a status this build
 *  has not heard of is better shown than swallowed. */
const fallback = 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'

export function OrderStatusBadge({ status }: { status: string }) {
  return (
    <span className={cn(base, ORDER_TONES[status] ?? fallback)}>
      {ORDER_LABELS[status] ?? status}
    </span>
  )
}

export function PaymentStatusBadge({ status }: { status: string }) {
  return (
    <span className={cn(base, PAYMENT_TONES[status] ?? fallback)}>
      {PAYMENT_LABELS[status] ?? status}
    </span>
  )
}
